import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import logo from './assets/cc_logo.jpg';

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
  20%, 40%, 60%, 80% { transform: translateX(10px); }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Roboto', sans-serif;
    background-color: #f1f3f4;
    margin: 0;
    padding: 0;
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const LoginForm = styled.div`
  background-color: white;
  padding: 48px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 360px;
  animation: ${props => props.shake ? shake : 'none'} 0.5s cubic-bezier(.36,.07,.19,.97) both;
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 24px;
  height: 100px;
`;

const Title = styled.h2`
  color: #202124;
  font-size: 24px;
  font-weight: 400;
  margin-bottom: 32px;
  text-align: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 16px;
  &:focus {
    outline: none;
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 16px;
  background-color: #1a73e8;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: #1765cc;
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
  animation: ${fadeOut} 2s ease-out forwards;
`;

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (shake) {
      const timer = setTimeout(() => setShake(false), 500);
      return () => clearTimeout(timer);
    }
  }, [shake]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch('https://cctexasfinance.com:9090/session/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error('Login failed');
      }

      // Guardamos la información relevante del usuario
      localStorage.setItem('session', data.session);
      localStorage.setItem('username', data.user); // Cambiado de username a data.user
      localStorage.setItem('userGroups', JSON.stringify(data.member)); // Guardamos los grupos del usuario
      
      if (onLogin) {
        onLogin();
      }
      
      // Navegamos al dashboard en lugar de /w2
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid username or password');
      setShake(true);
    }
  };

  return (
    <>
      <GlobalStyle />
      <Container>
        <LoginForm shake={shake}>
          <Logo src={logo} alt="Logo" />
          <Title>Login</Title>
          <form onSubmit={handleSubmit}>
            <Input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <Button type="submit">Login</Button>
          </form>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </LoginForm>
      </Container>
    </>
  );
};

export default Login;